import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaKey, FaEye, FaEyeSlash, FaCheckCircle } from "react-icons/fa";
import LeftPanel from "./LeftPanel";
import apiClient from "../common/apiClient";

const ResetPassword = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const mobile = location.state?.mobile;

    useEffect(() => {
        if (!mobile) {
            navigate("/forgetpassword");
        }
    }, [mobile, navigate]);

    const handlePasswordChange = (e) => {
        setNewPassword(e.target.value);
        if (error) setError("");
        if (success) setSuccess("");
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (error) setError("");
        if (success) setSuccess("");
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setLoading(true);

        if (newPassword.length < 6) {
            setError("Password must be at least 6 characters long.");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            setLoading(false);
            return;
        }

        try {
            const response = await apiClient(
                "post",
                "/cjidnvij/ceksfbuebijn/user/forgot-password/reset",
                { mobile, newPassword }
            );

            if (response.success && response.data.success) {
                setSuccess("Password reset successful! Redirecting to login...");
                setTimeout(() => {
                    navigate("/login");
                }, 3000);
            } else {
                setError(response.data?.message || response.error?.message || "Failed to reset password. Please try again.");
            }
        } catch (err) {
            setError("An unexpected error occurred. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex h-screen w-full overflow-hidden">
            <LeftPanel />
            <form
                onSubmit={handleSubmit}
                className="mx-4 lg:mx-28 mb-28 flex items-center"
            >
                <div className="flex-1 max-w-lg rounded-lg mt-10">
                    {/* <p className="text-4xl text-gray-800 mb-1">Welcome to</p>
                    <p className="text-5xl font-bold text-[#503691] mb-4">PRObroker</p> */}
                    <p className="text-3xl font-bold mb-4">Reset Password</p>
                    <p className="text-gray-600 mb-6">Create a new secure password for your account.</p>

                    <div className="relative mb-4">
                        <FaKey className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            value={newPassword}
                            onChange={handlePasswordChange}
                            className="w-full px-10 py-3 border border-gray-300 rounded-md"
                            required
                        />
                        <button
                            type="button"
                            className="absolute top-1/2 right-4 transform -translate-y-1/2 text-[#503691] focus:outline-none"
                            onClick={togglePasswordVisibility}
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>

                    <div className="relative mb-4">
                        <FaKey className="absolute top-1/2 left-4 transform -translate-y-1/2 text-[#503691]" />
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            className={`w-full px-10 py-3 border rounded-md ${confirmPassword && newPassword === confirmPassword
                                ? "border-green-500 focus:ring-green-500"
                                : "border-gray-300"
                                }`}
                            required
                        />
                        {confirmPassword && newPassword === confirmPassword && (
                            <FaCheckCircle className="absolute top-1/2 right-4 transform -translate-y-1/2 text-green-500" />
                        )}
                    </div>

                    {success && (
                        <div className="text-green-600 bg-green-50 border border-green-200 rounded-lg p-3 text-center mb-4 shadow-sm">
                            <div className="flex items-center justify-center gap-2">
                                <FaCheckCircle className="w-5 h-5 text-green-500" />
                                <span className="font-medium">{success}</span>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center mb-4 shadow-sm">
                            <div className="flex items-center justify-center gap-2">
                                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">{error}</span>
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="w-full h-11 bg-[#503691] text-white rounded-full font-semibold hover:bg-[#5b54e6] disabled:opacity-50"
                        disabled={loading}
                    >
                        {loading ? "Resetting..." : "Reset Password"}
                    </button>

                    <div className="text-center mt-6">
                        <Link to="/login" className="text-[#503691] font-semibold">
                            Cancel
                        </Link>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ResetPassword;
